import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, colones, fmtFecha, weekRange, toLocalISO } from "@/lib/api";
import { computePayrollForEmployee, aplicaJornada48, ACTIVIDAD_JORNADAS_ESPECIALES, cuentasSalario } from "@/lib/payroll";
import { exportReporteJornada48 } from "@/lib/reporteJornada48";
import { aplicaRenta, rentaPeriodo, aplicarRentaACalc } from "@/lib/renta";
import HorasJornadaCell from "@/components/HorasJornadaCell";
import { FileText, ChevronLeft, ChevronRight, Calendar, FileSpreadsheet, FileDown, FileArchive, Mail, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import ConceptoCell, { AusenteCell } from "@/components/ConceptoCell";
import { matchesFrecuencia } from "@/lib/frecuencia";
import { buildPuestosMap, puestoNoHorasExtras } from "@/lib/puestos";
import { generarReportePlanilla } from "@/lib/reporteExcel";
import { generarBoletaPago } from "@/lib/boletaPDF";
import { exportReportePagoExcel, exportReportePagoPDF } from "@/lib/reportePago";
import JSZip from "jszip";
import PlanillaEditHorasModal from "@/components/PlanillaEditHorasModal";
import { Pencil } from "lucide-react";
import { toast } from "sonner";

// Explica por qué la CCSS puede ser menor al % del bruto.
function ccssDetalle(calc, emp, empresa) {
  const b = calc.breakdown || {};
  const l = [`Base CCSS: ${colones(calc.ccssBase || 0)} × ${((calc.ccssRate || 0) * 100).toFixed(2)}% = ${colones(calc.ccss || 0)}`];
  if (!emp.ccss_aplica) l.push("El trabajador no tiene activo 'Aplica CCSS'");
  if (!empresa.ccss_aplica_extras && calc.montoExt > 0) l.push(`No cotizan las horas extras: ${colones(calc.montoExt)} (opción de la empresa)`);
  if (b.incapacidadCCSS?.monto > 0) l.push(`No cotiza la incapacidad: ${colones(b.incapacidadCCSS.monto)}`);
  if (calc.ccssFraccion < 1) l.push(`Solo cotiza lo devengado desde el ingreso a CCSS (${emp.fecha_ccss})`);
  return l.join("\n");
}

function ReporteMenuBtn({ label, testId, disabled, onSelect }) {
  const [open, setOpen] = useState(false);
  const pick = (f) => { setOpen(false); onSelect(f); };
  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)} disabled={disabled} className="np-btn np-btn-outline inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`${testId}-btn`}>
        <FileSpreadsheet size={16} /> {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-white border border-c rounded-xl shadow-lg z-40 overflow-hidden fade-in-up" data-testid={`${testId}-menu`}>
            <button onClick={() => pick("pdf")} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAFAF7] flex items-center gap-2" data-testid={`${testId}-pdf`}><FileText size={14} /> Descargar PDF</button>
            <button onClick={() => pick("excel")} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAFAF7] flex items-center gap-2 border-t border-c" data-testid={`${testId}-excel`}><FileSpreadsheet size={14} /> Descargar Excel</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Planillas() {
  const [tipo, setTipo] = useState("semanal"); // semanal | quincenal
  const [offset, setOffset] = useState(0);
  const [empleados, setEmpleados] = useState([]);
  const [asistencia, setAsistencia] = useState([]);
  const [rebajos, setRebajos] = useState([]);
  const [bonificaciones, setBonificaciones] = useState([]);
  const [empresa, setEmpresa] = useState({});
  // Tracking de envíos por email — clave: empleado.id
  const [emailStatus, setEmailStatus] = useState({}); // { [empId]: { state, cid, last_status, sent_to } }
  const [sendConfirm, setSendConfirm] = useState(null); // { row, cid }
  // Overrides manuales de horas por empleado (Planilla Editable) — se resetean al cambiar de período
  const [prevQuincena, setPrevQuincena] = useState(null);
  const [overrides, setOverrides] = useState({}); // { [empId]: { ord: {Diurna, Mixta, Nocturna}, ext: {...}, feriado } }
  const [editHoras, setEditHoras] = useState(null); // { row } — modal state

  const range = useMemo(() => {
    if (tipo === "semanal") {
      const r = weekRange(offset);
      return { start: r.start, end: r.end, label: `Semana del ${fmtFecha(r.start)} al ${fmtFecha(r.end)}` };
    } else {
      // Quincenal: 1-15 or 16-end
      const d = new Date();
      d.setMonth(d.getMonth() + Math.floor(offset / 2));
      const half = ((offset % 2) + 2) % 2 === 0; // 0=primera, 1=segunda
      const y = d.getFullYear(); const m = d.getMonth();
      let start, end;
      if (half) { start = new Date(y, m, 1); end = new Date(y, m, 15); }
      else { start = new Date(y, m, 16); end = new Date(y, m + 1, 0); }
      return { start: toLocalISO(start), end: toLocalISO(end), label: `${half?"Primera":"Segunda"} quincena de ${start.toLocaleDateString("es-CR", { month:"long", year:"numeric"})}` };
    }
  }, [tipo, offset]);

  // Detecta si el período seleccionado es "histórico" (anterior a la fecha de
  // registro de la empresa). Para estos períodos permitimos edición manual de
  // todos los empleados (histórico retroactivo para calcular aguinaldo).
  const isHistorico = useMemo(() => {
    if (!empresa?.created_at || !range.end) return false;
    const empRegISO = String(empresa.created_at).slice(0, 10);
    return range.end < empRegISO;
  }, [empresa?.created_at, range.end]);

  // Límite retroactivo mínimo: Diciembre 1 del año anterior (inicio del
  // período de aguinaldo actual — Dic(y-1) → Nov(y)). El usuario puede
  // navegar hasta ahí para cargar historial de aguinaldo.
  const minRetroDate = useMemo(() => {
    const hoy = new Date();
    return `${hoy.getFullYear() - 1}-12-01`;
  }, []);

  const canGoBack = range.start > minRetroDate;

  useEffect(() => {
    (async () => {
      const [e, a, r, b, emp, ovr] = await Promise.all([
        api.get("/empleados"),
        api.get(`/asistencia?fecha_inicio=${range.start}&fecha_fin=${range.end}`),
        api.get("/rebajos"),
        api.get("/bonificaciones"),
        api.get("/empresa"),
        api.get(`/planilla-overrides?fecha_inicio=${range.start}&fecha_fin=${range.end}`),
      ]);
      setEmpleados(e.data); setAsistencia(a.data); setRebajos(r.data); setBonificaciones(b.data); setEmpresa(emp.data || {});
      // Reconstruir el mapa de overrides desde el backend para el período actual
      const ovrMap = {};
      (ovr.data || []).forEach(o => {
        if (o?.empleado_id) ovrMap[o.empleado_id] = {
          ord: o.ord, ext: o.ext, feriado: o.feriado,
          dias_libres: o.dias_libres,
          dias_feriado: o.dias_feriado,
          dias_incapacidad_ccss: o.dias_incapacidad_ccss,
          dias_vacaciones: o.dias_vacaciones,
          dias_licencia: o.dias_licencia,
          ccss_override: o.ccss_override,
          rebajos_override: o.rebajos_override,
        };
      });
      setOverrides(ovrMap);
      // II quincena: datos de la I quincena para el Impuesto sobre la Renta (modo "solo II quincena")
      if (tipo === "quincenal" && range.start.slice(8, 10) === "16") {
        const pStart = `${range.start.slice(0, 8)}01`, pEnd = `${range.start.slice(0, 8)}15`;
        const [pa, po] = await Promise.all([
          api.get(`/asistencia?fecha_inicio=${pStart}&fecha_fin=${pEnd}`),
          api.get(`/planilla-overrides?fecha_inicio=${pStart}&fecha_fin=${pEnd}`),
        ]);
        const pMap = {};
        (po.data || []).forEach(o => { if (o?.empleado_id) pMap[o.empleado_id] = { ...o }; });
        setPrevQuincena({ start: pStart, end: pEnd, asistencia: pa.data || [], overrides: pMap });
      } else setPrevQuincena(null);
    })();
    // range.start/end are the only reactive deps; api, state setters and const helpers are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  const calculations = useMemo(() => {
    const puestosMap = buildPuestosMap(empresa);
    const grupoAct = empresa?.actividad_grupo;
    const ccssPensPct = Number(empresa?.ccss_pensionado_pct);
    const ccssPensRate = (isFinite(ccssPensPct) && ccssPensPct > 0) ? ccssPensPct / 100 : null;
    return empleados.filter(e => {
      if (!matchesFrecuencia(e, tipo)) return false;
      // Sólo mostrar empleados cuya fecha_ingreso sea <= fin del período.
      // Si no tiene fecha_ingreso registrada, se muestra por retro-compatibilidad.
      if (e.fecha_ingreso && String(e.fecha_ingreso).slice(0, 10) > range.end) return false;
      // Regla: si está activo → incluir. Si está inactivo (despedido/renuncia) →
      // incluir SOLO si su fecha_salida cae dentro o después del período visible
      // (trabajó parte de este período). Necesario para exclusión CCSS.
      if (e.estado === "Activo") return true;
      if (e.estado === "Inactivo") {
        const fs = e.fecha_salida ? String(e.fecha_salida).slice(0, 10) : "";
        if (fs && fs >= range.start) return true;
      }
      return false;
    }).map(emp => {
      const noHorasExtras = puestoNoHorasExtras(puestosMap, emp.puesto);
      const freq = String(emp.frecuencia_pago || "Mensual").toLowerCase();
      const diasBase = (grupoAct === "servicios_industria" && freq === "semanal") ? 26 : 30;
      const ccssRate = ccssPensRate;
      const calcPeriodo = (astAll, ovrMap, start, end, historico) => {
        const ast = astAll.filter(a => a.empleado_id === emp.id);
        const rebs = rebajos.filter(r => {
          if (r.empleado_id !== emp.id || !r.activo) return false;
          const f = r.fecha || r.fecha_inicio;
          return f && f >= start && f <= end;
        });
        const bons = bonificaciones.filter(b => {
          if (b.empleado_id !== emp.id || !b.activo) return false;
          return b.fecha && b.fecha >= start && b.fecha <= end;
        });
        const override = ((emp.planilla_editable || historico) && ovrMap[emp.id]) ? ovrMap[emp.id] : null;
        const calc = computePayrollForEmployee(emp, ast, { rebajos: rebs, bonificaciones: bons, extrasDoblesFeriado: !!empresa.extras_dobles_feriado, ccssAplicaExtras: !!empresa.ccss_aplica_extras, noHorasExtras, diasBase, ccssRate, override, periodStart: start, periodEnd: end, jornada48: aplicaJornada48(empresa, emp) });
        return { ast, rebs, bons, calc, override };
      };
      const { ast, rebs, bons, calc, override } = calcPeriodo(asistencia, overrides, range.start, range.end, isHistorico);
      if (aplicaRenta(empresa, emp)) {
        const empReg = empresa?.created_at ? String(empresa.created_at).slice(0, 10) : "";
        const brutoPrimera = prevQuincena
          ? calcPeriodo(prevQuincena.asistencia, prevQuincena.overrides, prevQuincena.start, prevQuincena.end, !!empReg && prevQuincena.end < empReg).calc.totalBruto
          : 0;
        aplicarRentaACalc(calc, rentaPeriodo({ empresa, emp, calc, periodStart: range.start, periodEnd: range.end, brutoPrimera }));
      }
      return { emp, ast, rebs, bons, calc, noHorasExtras, diasBase, override };
    });
  }, [empleados, asistencia, rebajos, bonificaciones, range.start, range.end, tipo, empresa, overrides, isHistorico, prevQuincena]);

  const totals = useMemo(() => calculations.reduce((acc, c) => ({
    bruto: acc.bruto + c.calc.totalBruto,
    extras: acc.extras + c.calc.montoExt,
    rebajos: acc.rebajos + c.calc.totalRebajos,
    ccss: acc.ccss + c.calc.ccss,
    renta: acc.renta + (c.calc.renta || 0),
    neto: acc.neto + c.calc.neto,
  }), { bruto: 0, extras: 0, rebajos: 0, ccss: 0, renta: 0, neto: 0 }), [calculations]);

  const exportarExcel = () => {
    const safeLabel = range.label.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").replace(/\s+/g, "_");
    generarReportePlanilla({
      kind: tipo, // "semanal" | "quincenal"
      empresa,
      calculations,
      totals,
      periodoLabel: range.label,
      fileNameLabel: `Planilla_${tipo}_${safeLabel}`,
      sheetNameLabel: tipo === "semanal" ? "Semanal" : "Quincenal",
    });
  };

  // Reporte de Pago (formato compacto para pago) — Excel o PDF
  const [pagoMenu, setPagoMenu] = useState(false);
  const exportarPago = (formato) => {
    const safeLabel = range.label.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").replace(/\s+/g, "_");
    const opts = {
      empresa, calculations, totals,
      periodoLabel: range.label,
      fileNameLabel: `Reporte_de_Pago_${tipo}_${safeLabel}`,
      sheetNameLabel: tipo === "semanal" ? "Semanal" : "Quincenal",
      periodEnd: range.end,
    };
    if (formato === "pdf") exportReportePagoPDF(opts);
    else exportReportePagoExcel(opts);
    setPagoMenu(false);
  };

  const showJornada48 = tipo === "quincenal" && empresa?.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES;
  const showRenta = showJornada48;
  const exportarJornada48 = (kind, formato) => {
    const filas = calculations;
    const safeLabel = range.label.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").replace(/\s+/g, "_");
    exportReporteJornada48({ kind, formato, empresa, calculations: filas, periodoLabel: range.label, fileLabel: safeLabel, periodEnd: range.end });
  };

  // Upsert del comprobante en backend y devuelve el id creado.
  const guardarComprobante = async ({ emp, calc, override }, result) => {
    try {
      const { data } = await api.post("/comprobantes", {
        empleado_id: emp.id,
        periodo_label: range.label,
        kind: tipo,
        fecha_inicio: range.start,
        fecha_fin: range.end,
        monto_bruto: calc.totalBruto,
        monto_ccss: calc.ccss,
        monto_renta: calc.renta || 0,
        monto_rebajos: calc.totalRebajos,
        monto_neto: calc.neto,
        file_name: result.fileName,
        pdf_base64: result.pdfBase64,
        override_horas: override || null,
        planilla_editable: !!emp.planilla_editable,
      });
      return data?.id;
    } catch (e) {
      console.error("No se pudo archivar el comprobante:", e);
      return null;
    }
  };

  const descargarBoleta = async (row) => {
    const result = generarBoletaPago({
      empresa, empleado: row.emp, calc: row.calc,
      asistencias: row.ast, rebajos: row.rebs, bonificaciones: row.bons,
      periodoLabel: range.label, kind: tipo, periodEnd: range.end,
    });
    await guardarComprobante(row, result);
  };

  // Envío masivo de comprobantes por correo. Genera PDFs, los guarda en backend
  // y luego dispara el envío en paralelo. Muestra resumen al final.
  const [sending, setSending] = useState(false);
  const [batchResult, setBatchResult] = useState(null); // { enviados: [], fallidos: [] }
  const [batchConfirm, setBatchConfirm] = useState(false);
  const enviarComprobantesLote = async () => {
    setBatchConfirm(false);
    if (calculations.length === 0) return;
    const conCorreo = calculations.filter(r => (r.emp.correo || "").trim());
    if (conCorreo.length === 0) {
      toast.error("Ninguno de los empleados del período tiene correo registrado");
      return;
    }
    setSending(true);
    try {
      const ids = [];
      // 1) Generar y guardar cada comprobante para obtener su cid
      for (const row of conCorreo) {
        const result = generarBoletaPago({
          empresa, empleado: row.emp, calc: row.calc,
          asistencias: row.ast, rebajos: row.rebs, bonificaciones: row.bons,
          periodoLabel: range.label, kind: tipo, periodEnd: range.end, skipDownload: true,
        });
        const cid = await guardarComprobante(row, result);
        if (cid) ids.push(cid);
      }
      // 2) Disparar el envío masivo en el backend
      const { data } = await api.post("/comprobantes/enviar-lote", { ids });
      setBatchResult(data);
      const ok = (data.enviados || []).length;
      const ko = (data.fallidos || []).length;
      if (ok > 0 && ko === 0) toast.success(`Enviados ${ok} comprobante(s) sin errores`);
      else if (ok > 0 && ko > 0) toast.warning(`${ok} enviados, ${ko} con error`);
      else toast.error(`No se pudo enviar ningún comprobante`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al enviar el lote";
      toast.error(typeof detail === "string" ? detail : "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  // Descarga todos los comprobantes del período en un único archivo .zip.
  // Para cada empleado se genera el PDF sin auto-descargar (skipDownload) y se
  // agrega al zip. Además, cada comprobante se archiva en el backend igual que
  // cuando se descarga individualmente (para el histórico).
  const [zipping, setZipping] = useState(false);
  const descargarTodosZip = async () => {
    if (calculations.length === 0) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      const safePeriod = range.label.replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").replace(/\s+/g, "_");
      for (const row of calculations) {
        const result = generarBoletaPago({
          empresa, empleado: row.emp, calc: row.calc,
          asistencias: row.ast, rebajos: row.rebs, bonificaciones: row.bons,
          periodoLabel: range.label, kind: tipo, periodEnd: range.end, skipDownload: true,
        });
        // Añadir el PDF al zip
        zip.file(result.fileName, result.pdfBase64, { base64: true });
        // Archivar en backend (histórico) — no bloquear el zip si falla
        await guardarComprobante(row, result);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Comprobantes_${tipo}_${safePeriod}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Descargados ${calculations.length} comprobante(s) en ZIP`);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo generar el ZIP");
    } finally {
      setZipping(false);
    }
  };

  // Genera el PDF, lo guarda en backend y abre el modal de confirmación.
  const prepararEnvio = async (row) => {
    const { emp } = row;
    if (!emp.correo) {
      toast.error(`${emp.nombre} no tiene correo registrado`);
      return;
    }
    if (!empresa.correo) {
      toast.warning("La empresa no tiene un correo de respuesta configurado. Se enviará igual.");
    }
    setEmailStatus(s => ({ ...s, [emp.id]: { ...(s[emp.id] || {}), state: "preparing" } }));
    const result = generarBoletaPago({
      empresa, empleado: row.emp, calc: row.calc,
      asistencias: row.ast, rebajos: row.rebs, bonificaciones: row.bons,
      periodoLabel: range.label, kind: tipo, periodEnd: range.end, skipDownload: true,
    });
    const cid = await guardarComprobante(row, result);
    if (!cid) {
      toast.error("No se pudo preparar el comprobante");
      setEmailStatus(s => ({ ...s, [emp.id]: { state: "error" } }));
      return;
    }
    setEmailStatus(s => ({ ...s, [emp.id]: { ...(s[emp.id] || {}), state: "ready", cid } }));
    setSendConfirm({ row, cid });
  };

  const confirmarEnvio = async () => {
    if (!sendConfirm) return;
    const { row, cid } = sendConfirm;
    const empId = row.emp.id;
    setSendConfirm(null);
    setEmailStatus(s => ({ ...s, [empId]: { ...(s[empId] || {}), state: "sending", cid } }));
    try {
      const { data } = await api.post(`/comprobantes/${cid}/send-email`);
      toast.success(`Comprobante enviado a ${data.sent_to}`);
      setEmailStatus(s => ({ ...s, [empId]: { state: "sent", cid, last_status: data.last_status, sent_to: data.sent_to, email_id: data.email_id } }));
      // Poll de estado a los 4s y nuevamente a los 15s — Resend a veces tarda en reportar 'delivered'
      setTimeout(() => refrescarEstado(empId, cid), 4000);
      setTimeout(() => refrescarEstado(empId, cid), 15000);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al enviar el correo";
      toast.error(detail.length > 160 ? detail.slice(0, 160) + "..." : detail);
      setEmailStatus(s => ({ ...s, [empId]: { state: "error", cid, error: detail } }));
    }
  };

  const refrescarEstado = async (empId, cid) => {
    try {
      const { data } = await api.get(`/comprobantes/${cid}/email-status`);
      setEmailStatus(s => ({
        ...s,
        [empId]: { ...(s[empId] || {}), state: "sent", last_status: data.status, sent_to: data.sent_to, email_id: data.email_id },
      }));
    } catch (err) { console.error("Planillas: no se pudo obtener email-status", err); }
  };

  // Determina el color del botón del sobre en función del estado de entrega
  // - 'error' o rebote/falla → rojo
  // - 'sent' + delivered / sent → verde
  // - 'sending' → sin color extra
  // - sin estado → gris/default
  const emailButtonState = (empId) => {
    const st = emailStatus[empId];
    if (!st) return "default";
    if (st.state === "error") return "red";
    if (st.state === "sending" || st.state === "preparing") return "loading";
    if (st.state === "sent") {
      const ls = (st.last_status || "").toLowerCase();
      if (ls.includes("bounce") || ls.includes("complain") || ls.includes("fail")) return "red";
      // 'sent', 'delivered', 'email.delivered', o cualquier estado positivo
      return "green";
    }
    return "default";
  };

  // Render del badge de estado (icono + tooltip)
  const renderEmailIcon = (empId) => {
    const st = emailStatus[empId];
    if (!st) return <Mail size={14} />;
    if (st.state === "preparing" || st.state === "sending") return <Loader2 size={14} className="animate-spin" />;
    if (st.state === "error") return <AlertTriangle size={14} />;
    if (st.state === "sent") {
      const ls = (st.last_status || "").toLowerCase();
      if (ls.includes("bounce") || ls.includes("complain") || ls.includes("fail")) return <AlertTriangle size={14} />;
      return <CheckCircle2 size={14} />;
    }
    return <Mail size={14} />;
  };

  const renderEmailTitle = (row) => {
    const st = emailStatus[row.emp.id];
    if (!st) return `Enviar comprobante a ${row.emp.correo || "(sin correo)"}`;
    if (st.state === "sending" || st.state === "preparing") return "Enviando...";
    if (st.state === "sent") {
      const ls = (st.last_status || "").toLowerCase();
      const label = ls.includes("deliver") ? "Entregado ✓" : ls.includes("bounce") || ls.includes("complain") ? "Rebotó ⚠️" : "Enviado (pendiente confirmación)";
      return `${label} → ${st.sent_to}`;
    }
    if (st.state === "error") return `Error: ${st.error || "intenta de nuevo"}`;
    return "Enviar comprobante";
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><FileText size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Planillas</h1>
            <p className="text-sm text-secondary-c capitalize">{range.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={() => setBatchConfirm(true)}
            disabled={calculations.length === 0 || sending}
            className="np-btn np-btn-primary inline-flex items-center gap-2"
            data-testid="enviar-todos-btn"
            title="Enviar los comprobantes del período a cada empleado con correo registrado"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            {sending ? "Enviando…" : `Enviar a todos`}
          </button>
          <button
            onClick={descargarTodosZip}
            disabled={calculations.length === 0 || zipping}
            className="np-btn np-btn-outline inline-flex items-center gap-2"
            data-testid="descargar-zip-btn"
            title="Descargar todos los comprobantes del período en un archivo .zip"
          >
            {zipping ? <Loader2 size={16} className="animate-spin" /> : <FileArchive size={16} />}
            {zipping ? `Generando…` : `Descargar ZIP (${calculations.length})`}
          </button>
          <div className="relative">
            <button
              onClick={() => setPagoMenu(v => !v)}
              disabled={calculations.length === 0}
              className="np-btn np-btn-outline inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reporte-pago-btn"
            >
              <FileSpreadsheet size={16} /> Reporte de Pago
            </button>
            {pagoMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setPagoMenu(false)} />
                <div className="absolute right-0 mt-1 w-44 bg-white border border-c rounded-xl shadow-lg z-40 overflow-hidden fade-in-up" data-testid="reporte-pago-menu">
                  <button onClick={() => exportarPago("pdf")} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAFAF7] flex items-center gap-2" data-testid="reporte-pago-pdf">
                    <FileText size={14} /> Descargar PDF
                  </button>
                  <button onClick={() => exportarPago("excel")} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAFAF7] flex items-center gap-2 border-t border-c" data-testid="reporte-pago-excel">
                    <FileSpreadsheet size={14} /> Descargar Excel
                  </button>
                </div>
              </>
            )}
          </div>
          {showJornada48 && (
            <>
              <ReporteMenuBtn label="Reporte Salario (sin extras)" testId="reporte-salario-sin-extras" disabled={calculations.length === 0} onSelect={f => exportarJornada48("salario", f)} />
              <ReporteMenuBtn label="Reporte Horas Extras" testId="reporte-horas-extras" disabled={calculations.length === 0} onSelect={f => exportarJornada48("extras", f)} />
            </>
          )}
          <button
            onClick={exportarExcel}
            disabled={calculations.length === 0}
            className="np-btn np-btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet size={16} /> Reporte
          </button>
        </div>
      </header>

      <div className="np-card p-4 mb-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="inline-flex rounded-xl bg-[#EFEEE8] p-1">
          <button onClick={() => { setTipo("semanal"); setOffset(0); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "semanal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="tab-semanal">Semanal</button>
          <button onClick={() => { setTipo("quincenal"); setOffset(0); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "quincenal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="tab-quincenal">Quincenal</button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOffset(o => o - 1)} disabled={!canGoBack} className="np-btn np-btn-outline p-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="planilla-prev-btn" title={!canGoBack ? `No se puede navegar antes de ${minRetroDate}` : "Período anterior"}><ChevronLeft size={16} /></button>
          <div className="flex items-center gap-2 px-4 py-2 bg-[#FAFAF7] rounded-xl text-sm font-medium">
            <Calendar size={14} /> {range.label}
            {isHistorico && (
              <span className="ml-1 text-[10px] font-semibold bg-[#FFF7E6] text-[#B57F1A] px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="planilla-historica-badge">Histórica</span>
            )}
          </div>
          <button onClick={() => setOffset(o => o + 1)} className="np-btn np-btn-outline p-2" data-testid="planilla-next-btn"><ChevronRight size={16} /></button>
        </div>
        <div className="flex gap-4 text-sm flex-wrap">
          <div><span className="text-secondary-c">Total bruto:</span> <span className="font-semibold text-primary-c">{colones(totals.bruto)}</span></div>
          <div><span className="text-secondary-c">Extras:</span> <span className="font-semibold text-warning">{colones(totals.extras)}</span></div>
          <div><span className="text-secondary-c">Rebajos:</span> <span className="font-semibold text-error">{colones(totals.rebajos + totals.ccss + totals.renta)}</span></div>
          <div><span className="text-secondary-c">Neto:</span> <span className="font-semibold text-success" data-testid="planilla-neto-total">{colones(totals.neto)}</span></div>
        </div>
      </div>

      <div className="np-card p-0 np-scroll">
        <table className="np-table np-table-compact">
          <thead><tr>
            <th>Empleado</th>
            <th>Identificación</th>
            <th>Hrs Ord</th>
            <th>Hrs Ext</th>
            <th>Día Libre</th>
            <th>Feriado</th>
            <th>Incapacidad</th>
            <th>Vacaciones</th>
            <th>Licencia</th>
            <th>Ausente</th>
            <th>Bonificaciones</th>
            <th>Monto Ext</th>
            <th>Bruto</th>
            <th>CCSS</th>
            {showRenta && <th>Imp. Renta</th>}
            <th>Rebajos</th>
            <th>Neto</th>
            <th></th>
          </tr></thead>
          <tbody>
            {calculations.map((row) => {
              const { emp, calc } = row;
              const b = calc.breakdown || {};
              const incSub = b.incapacidadCCSS && b.incapacidadCCSS.diasNoPagos ? `${b.incapacidadCCSS.diasNoPagos} no pago(s)` : null;
              return (
              <tr key={emp.id} data-testid={`planilla-row-${emp.cedula}`}>
                <td><div className="font-medium">{emp.nombre} {emp.apellidos}</div><div className="text-xs text-secondary-c">{emp.puesto} · {emp.departamento}</div>{cuentasSalario(emp, range.end) && <span className="np-badge np-badge-neutral mt-1 inline-block text-[10px]" data-testid={`planilla-2cuentas-${emp.cedula}`}>2 cuentas</span>}</td>
                <td className="text-xs whitespace-nowrap" data-testid={`planilla-cedula-${emp.cedula}`}>{emp.cedula || "—"}</td>
                <td><HorasJornadaCell byJornada={calc.byJornadaOrd} showMonto /></td>
                <td><HorasJornadaCell byJornada={calc.byJornadaExt} showMonto montoColor="text-warning" /></td>
                <td><ConceptoCell dias={b.diaLibre?.dias} monto={b.diaLibre?.monto} /></td>
                <td><ConceptoCell dias={b.feriado?.dias} monto={b.feriado?.monto} /></td>
                <td><ConceptoCell dias={b.incapacidadCCSS?.dias} monto={b.incapacidadCCSS?.monto} sub={incSub} /></td>
                <td><ConceptoCell dias={b.vacaciones?.dias} monto={b.vacaciones?.monto} /></td>
                <td><ConceptoCell dias={b.licencia?.dias} monto={b.licencia?.monto} /></td>
                <td><AusenteCell dias={b.ausente?.dias} testId={`planilla-ausente-${emp.cedula}`} /></td>
                <td>
                  {b.bonificaciones?.cantidad ? (
                    <div>
                      <div className="font-medium text-xs">{b.bonificaciones.cantidad} {b.bonificaciones.cantidad === 1 ? "bono" : "bonos"}</div>
                      <div className="text-[11px] text-success font-medium">+{colones(b.bonificaciones.monto)}</div>
                      {b.bonificaciones.montoCCSS > 0 && <div className="text-[10px] text-error">CCSS: {colones(b.bonificaciones.montoCCSS)}</div>}
                    </div>
                  ) : <span className="text-secondary-c">—</span>}
                </td>
                <td className={calc.montoExt > 0 ? "text-warning font-medium" : ""}>{calc.montoExt ? colones(calc.montoExt) : "—"}</td>
                <td className="font-medium">{colones(calc.totalBruto)}</td>
                <td className="text-error" title={ccssDetalle(calc, emp, empresa)} data-testid={`planilla-ccss-${emp.cedula}`}>{calc.ccss ? "−" + colones(calc.ccss) : "—"}</td>
                {showRenta && <td className="text-error" data-testid={`planilla-renta-${emp.cedula}`}>{calc.renta ? "−" + colones(calc.renta) : "—"}</td>}
                <td className="text-error">{calc.totalRebajos ? "−" + colones(calc.totalRebajos) : "—"}</td>
                <td className="font-semibold text-success">{colones(calc.neto)}</td>
                <td>
                  <div className="flex items-center justify-end gap-1.5">
                    {(emp.planilla_editable || isHistorico) && (
                      <button
                        onClick={() => setEditHoras(row)}
                        title={isHistorico ? "Editar planilla histórica (retroactiva)" : "Editar horas manualmente"}
                        className={`np-btn np-btn-outline p-1.5 transition-colors ${overrides[emp.id] ? "!bg-[var(--warning)] !border-[var(--warning)] !text-white hover:!bg-[#B57F1A]" : ""}`}
                        data-testid={`edit-horas-btn-${emp.cedula}`}
                        data-override-active={!!overrides[emp.id]}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => descargarBoleta(row)}
                      title="Descargar comprobante de pago"
                      className="np-btn np-btn-outline p-1.5"
                      data-testid={`boleta-pdf-${emp.cedula}`}
                    >
                      <FileDown size={14} />
                    </button>
                    <button
                      onClick={() => prepararEnvio(row)}
                      disabled={!emp.correo || emailStatus[emp.id]?.state === "sending" || emailStatus[emp.id]?.state === "preparing"}
                      title={renderEmailTitle(row)}
                      className={`np-btn np-btn-outline p-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
                        emailButtonState(emp.id) === "green"
                          ? "!bg-[var(--success)] !border-[var(--success)] !text-white hover:!bg-[#1F5A34]"
                          : emailButtonState(emp.id) === "red"
                            ? "!bg-[var(--error)] !border-[var(--error)] !text-white hover:!bg-[#993A3A]"
                            : ""
                      }`}
                      data-testid={`boleta-email-${emp.cedula}`}
                      data-email-state={emailButtonState(emp.id)}
                    >
                      {renderEmailIcon(emp.id)}
                    </button>
                  </div>
                </td>
              </tr>
            );})}
            <tr className="bg-[#FAFAF7] font-semibold">
              <td>Total General</td>
              <td colSpan={10}></td>
              <td className="text-warning">{colones(totals.extras)}</td>
              <td>{colones(totals.bruto)}</td>
              <td className="text-error">−{colones(totals.ccss)}</td>
              {showRenta && <td className="text-error" data-testid="planilla-total-renta">−{colones(totals.renta)}</td>}
              <td className="text-error">−{colones(totals.rebajos)}</td>
              <td className="text-success">{colones(totals.neto)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {sendConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSendConfirm(null)}>
          <div className="np-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="email-confirm-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
                <Mail size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">Enviar comprobante por correo</h3>
                <p className="text-xs text-secondary-c mt-0.5">Período: <strong>{range.label}</strong></p>
              </div>
            </div>
            <div className="space-y-2 text-sm bg-[#FAFAF7] border border-c rounded-xl p-3.5 mb-4">
              <div className="flex justify-between gap-3"><span className="text-secondary-c">Destinatario:</span><strong className="text-right truncate" data-testid="email-confirm-to">{sendConfirm.row.emp.correo}</strong></div>
              <div className="flex justify-between gap-3"><span className="text-secondary-c">Remitente:</span><strong className="text-right truncate">{empresa.razon_social || empresa.nombre || ""}</strong></div>
              {empresa.correo && <div className="flex justify-between gap-3"><span className="text-secondary-c">Reply-To:</span><strong className="text-right truncate">{empresa.correo}</strong></div>}
              <div className="flex justify-between gap-3"><span className="text-secondary-c">Neto:</span><strong className="text-right">{colones(sendConfirm.row.calc.neto)}</strong></div>
            </div>
            <p className="text-xs text-secondary-c mb-4">
              El trabajador recibirá el PDF como adjunto. Cuando responda al correo, su respuesta llegará al correo de la empresa.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSendConfirm(null)} className="np-btn np-btn-outline" data-testid="email-confirm-cancel">Cancelar</button>
              <button onClick={confirmarEnvio} className="np-btn np-btn-primary" data-testid="email-confirm-send">
                <Mail size={14} className="mr-1.5" /> Enviar ahora
              </button>
            </div>
          </div>
        </div>
      )}
      {editHoras && (
        <PlanillaEditHorasModal
          open={!!editHoras}
          onClose={() => setEditHoras(null)}
          empleado={editHoras.emp}
          calcActual={editHoras.calc}
          overrideActual={overrides[editHoras.emp.id]}
          historico={isHistorico}
          onSave={async (ov) => {
            const empId = editHoras.emp.id;
            // Actualización optimista
            setOverrides(s => ({ ...s, [empId]: ov }));
            setEditHoras(null);
            try {
              await api.post("/planilla-overrides", {
                empleado_id: empId,
                fecha_inicio: range.start,
                fecha_fin: range.end,
                ord: ov.ord, ext: ov.ext, feriado: ov.feriado,
                dias_libres: ov.dias_libres,
                dias_feriado: ov.dias_feriado,
                dias_incapacidad_ccss: ov.dias_incapacidad_ccss,
                dias_vacaciones: ov.dias_vacaciones,
                dias_licencia: ov.dias_licencia,
                ccss_override: ov.ccss_override,
                rebajos_override: ov.rebajos_override,
              });
              toast.success("Planilla guardada");
            } catch (e) {
              toast.error("No se pudo guardar la edición. Reintentá.");
            }
          }}
          onReset={async () => {
            const empId = editHoras.emp.id;
            setOverrides(s => { const n = { ...s }; delete n[empId]; return n; });
            setEditHoras(null);
            try {
              await api.delete(`/planilla-overrides?empleado_id=${empId}&fecha_inicio=${range.start}&fecha_fin=${range.end}`);
              toast.success("Valores automáticos restaurados");
            } catch (e) {
              toast.error("No se pudo restaurar. Reintentá.");
            }
          }}
        />
      )}

      {/* Confirmación de envío masivo */}
      {batchConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBatchConfirm(false)} data-testid="batch-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
                <Mail size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">Enviar comprobantes a todos</h3>
                <p className="text-xs text-secondary-c mt-0.5">Período: <strong>{range.label}</strong></p>
              </div>
            </div>
            {(() => {
              const conCorreo = calculations.filter(r => (r.emp.correo || "").trim());
              const sinCorreo = calculations.length - conCorreo.length;
              return (
                <>
                  <div className="space-y-2 text-sm bg-[#FAFAF7] border border-c rounded-xl p-3.5 mb-4">
                    <div className="flex justify-between gap-3"><span className="text-secondary-c">Empleados a enviar:</span><strong>{conCorreo.length}</strong></div>
                    {sinCorreo > 0 && <div className="flex justify-between gap-3"><span className="text-secondary-c">Sin correo (se omiten):</span><strong className="text-yellow-700">{sinCorreo}</strong></div>}
                    <div className="flex justify-between gap-3"><span className="text-secondary-c">Remitente:</span><strong className="text-right truncate">{empresa.razon_social || empresa.nombre || ""}</strong></div>
                  </div>
                  <p className="text-xs text-secondary-c mb-4">
                    Se generará el PDF de cada empleado y se enviará a su correo con la razón social de la empresa como remitente. Podría tomar unos segundos.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setBatchConfirm(false)} className="np-btn np-btn-outline" data-testid="batch-confirm-cancel">Cancelar</button>
                    <button onClick={enviarComprobantesLote} className="np-btn np-btn-primary" data-testid="batch-confirm-send" disabled={conCorreo.length === 0}>
                      <Mail size={14} className="mr-1.5" /> Enviar {conCorreo.length} comprobante(s)
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Resumen post-envío */}
      {batchResult && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBatchResult(null)} data-testid="batch-result-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">Resultado del envío masivo</h3>
                <p className="text-xs text-secondary-c mt-0.5">
                  <span className="text-primary-c font-semibold">{(batchResult.enviados || []).length} enviados</span>
                  {(batchResult.fallidos || []).length > 0 && <> · <span className="text-[#B84B4B] font-semibold">{batchResult.fallidos.length} con error</span></>}
                </p>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 -mx-2 px-2">
              {(batchResult.enviados || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-xs uppercase font-semibold text-secondary-c tracking-wider mb-2">Enviados ✓</div>
                  <ul className="text-sm space-y-1">
                    {batchResult.enviados.map((it, i) => (
                      <li key={i} className="flex justify-between gap-3 py-1 border-b border-c last:border-0">
                        <span className="truncate">{it.empleado}</span>
                        <span className="text-secondary-c truncate">{it.correo}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(batchResult.fallidos || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase font-semibold text-[#B84B4B] tracking-wider mb-2">Con error ✗</div>
                  <ul className="text-sm space-y-1">
                    {batchResult.fallidos.map((it, i) => (
                      <li key={i} className="py-1 border-b border-c last:border-0">
                        <div className="flex justify-between gap-3"><span className="truncate">{it.empleado}</span></div>
                        <div className="text-xs text-[#B84B4B]">{it.motivo}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setBatchResult(null)} className="np-btn np-btn-primary" data-testid="batch-result-close">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
